import React, { useEffect, useState, useRef } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
  Stack,
  Select,
  MenuItem,
  Pagination,
  Button,
  Snackbar,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip
} from "@mui/material";
import { formatPhoneNumber } from "../../../common/helpers/commonFun";
import { makeStyles } from "@mui/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { searchCarrierListAction, getAllCarrierAction, deleteCarrierListingByIdAction,
  searchManageCarrierListingAction } from "../../../redux/actions/agencyActions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Loader from "../../loader/loader"
import { groupStateList } from "../../../common/StaticFieldList";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 20,
    padding: 16,
    borderRadius: 8,
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    fontWeight: "bold",
    backgroundColor: "#b0b0b0",
    color: "white",
    textAlign: "center",
  },
  tableRow: {
    '&:hover': {
      backgroundColor: "#f1f1f1",
    },
  },
  iconButton: {
    marginRight: 8,
    '&:hover': {
      backgroundColor: "#e0e0e0",
    },
  },
  addButton: {
    color: "white",
    borderRadius: 8,
    textTransform: "none",
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
  },
  dialogContent: {
    backgroundColor: "#f8f8f8",
  },
  deleteButton: {
    backgroundColor: "#d32f2f",
    color: "white",
    "&:hover": {
      backgroundColor: "#c62828",
    },
  },
  cancelButton: {
    backgroundColor: "#757575",
    color: "white",
    "&:hover": {
      backgroundColor: "#616161",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#1565c0',
  color: theme.palette.common.white,
  fontFamily: '"Poppins", sans-serif',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& td, & th': {
    padding: theme.spacing(1.5),
    fontFamily: '"Poppins", sans-serif',
  },
}));

const ManageCarrierListing = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchPayload, setSearchPayload] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletingCarrierId, setDeletingCarrierId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedStates, setSelectedStates] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formError, setFormError] = useState("");

  const carrierDetails = useSelector((state) => state?.agency?.get_all_carrier_list?.content);
  const carrierDetails_pagination = useSelector((state) => state?.agency?.get_all_carrier_list);
  const searchedCarrierDetails = useSelector((state) => state?.agency?.get_all_manage_carrier_listing?.content);
  const searchedCarrierPagination = useSelector((state) => state?.agency?.get_all_manage_carrier_listing);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const currentData = isSearchActive ? searchedCarrierDetails : carrierDetails;
  const currentPagination = isSearchActive ? searchedCarrierPagination : carrierDetails_pagination;
  const carriers = currentData || [];
  const totalElements = currentPagination?.totalElements || 0;
  const totalPages = currentPagination?.totalPages || 0;

  const isFetchingCarrierRef = useRef(false);

  useEffect(() => {
    if (!isSearchActive && !isFetchingCarrierRef.current) {
      isFetchingCarrierRef.current = true;

      dispatch(getAllCarrierAction(page, rowsPerPage))
        .finally(() => {
          isFetchingCarrierRef.current = false;
        });
    }
  }, [dispatch, page, rowsPerPage, isSearchActive]);

  const handleSearch = async () => {
    const trimmedQuery = searchKeyword.trim();
    const stateCodes = selectedStates.length > 0
      ? selectedStates.map((s) => s.value)
      : null;

  if (!trimmedQuery && !stateCodes) {
    setFormError("Please enter a carrier name or select at least one state to search.");
    return;
  }
  setFormError("");

  const payload = { query: trimmedQuery !== "" ? trimmedQuery : null, stateCodes };

  setSearchPayload(payload);
  setPage(0);
  setIsSearchActive(true);
  setIsSearching(true);

  try {
    await dispatch(searchCarrierListAction(payload, 0, rowsPerPage));
  } catch (err) {
    console.error("Search failed:", err);
  } finally {
    setIsSearching(false);
  }
};

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const handleResetSearch = () => {
    setSearchKeyword('');
    setSelectedStates([]);
    setSearchPayload(null);
    setIsSearchActive(false);
    setPage(0);
  };

  const handleEdit = (carrier) => {
   
    const formattedCarrier = {
  ...carrier,
  phone: carrier.phone ? formatPhoneNumber(carrier.phone) : "",
  fax: carrier.fax ? formatPhoneNumber(carrier.fax) : "",
  alternatePhone: carrier.alternatePhone ? formatPhoneNumber(carrier.alternatePhone) : "",
};
    navigate('/Addinsurancecarrier', {
      state: {
        selectedUser: formattedCarrier,
      },
    });
  };

  const handleDeleteOpen = (id) => {
    setDeletingCarrierId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleDeleteConfirm = async () => {
    if (deletingCarrierId !== null) {
      setIsDeleting(true);
      setOpenDelete(false);
      try {
        await dispatch(deleteCarrierListingByIdAction(deletingCarrierId));

        if (isSearchActive && searchKeyword.trim()) {
          dispatch(searchManageCarrierListingAction(searchKeyword.trim(), page, rowsPerPage));
        } else {
          dispatch(getAllCarrierAction(page, rowsPerPage));
        }

        setSnackbar({
          open: true,
          message: 'Carrier deleted successfully',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'You do not have access to delete the carrier',
          severity: 'error',
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const addNewInsuranceCarrier = () => {
    navigate('/Addinsurancecarrier')
  }

const handleChangePage = async (event, newPage) => {
  setPage(newPage);

  if (isSearchActive && searchPayload) {
    setIsSearching(true);
    try {
      await dispatch(searchCarrierListAction(searchPayload, newPage, rowsPerPage));
    } catch (error) {
      console.error('Pagination search error:', error);
    } finally {
      setIsSearching(false);
    }
  }
};

const handleChangeRowsPerPage = async (event) => {
  const newRowsPerPage = parseInt(event.target.value, 10);
  setRowsPerPage(newRowsPerPage);
  setPage(0);

  if (isSearchActive && searchPayload) {
    setIsSearching(true);
    try {
      await dispatch(searchCarrierListAction(searchPayload, 0, newRowsPerPage));
    } catch (error) {
      console.error('Rows per page search error:', error);
    } finally {
      setIsSearching(false);
    }
  }
};

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePaginationChange = (event, value) => {
    handleChangePage(event, value - 1);
  };

  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Box sx={{
      padding: isMobile ? 1 : 2,
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <Paper sx={{
        margin: isMobile ? 0.5 : 1,
        padding: isMobile ? 1 : 2,
        border: "1px solid rgba(49, 49, 49, 0.1)",
        boxShadow: isMobile ? 1 : "0 4px 8px rgba(104, 104, 104, 0.1)",
      }}>
        <Box sx={{ padding: isMobile ? '12px' : '16px' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: isMobile ? '12px' : '16px',
            alignItems: isMobile ? 'flex-start' : 'end',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Typography
              variant={isMobile ? "h6" : "h2"}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: '500',
                fontSize: isMobile ? '18px' : '24px'
              }}
              gutterBottom
            >
              Manage Carriers ({totalElements})
            </Typography>
            <Button
              variant="contained"
              onClick={addNewInsuranceCarrier}
            >
              Add New
            </Button>
          </Box>
          <Box sx={{ marginBottom: isMobile ? "16px" : "20px" }}>
            <Box sx={{
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: 'space-between',
              width: '100%',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Box sx={{
                display: "flex",
                alignItems: isMobile ? 'flex-start' : 'end',
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto',
                gap: isMobile ? 2 : 0
              }}>

                <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                  <Typography
                    variant="body1"
                  >
                    Search Carrier By Name
                  </Typography>
                  <TextField
                    variant="outlined"
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                      if (formError) setFormError("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    placeholder="Enter carrier name..."
                    disabled={isSearching}
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>

                <Box sx={{
                  marginLeft: isMobile ? "0" : "10px",
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Typography
                    variant="body1"
                  >
                    Select Carrier States
                  </Typography>
                  <Autocomplete
                    multiple
                    limitTags={isMobile ? 1 : 2}
                    options={groupStateList}
                    getOptionLabel={(option) => option.label}
                    value={selectedStates}
                    onChange={(event, newValue) => {
                      setSelectedStates(newValue);
                      if (formError) setFormError("");
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select states"
                      />
                    )}
                    sx={{
                      minWidth: isMobile ? '100%' : 250,
                      '& .MuiChip-root': {
                        fontSize: isMobile ? '10px' : '12px',
                        height: isMobile ? '24px' : '32px'
                      }
                    }}
                  />
                </Box>

                <Box sx={{
                  display: "flex",
                  marginTop: isMobile ? "0" : "2px",
                  marginLeft: isMobile ? "0" : "10px",
                  width: isMobile ? '100%' : 'auto',
                  gap: isMobile ? 1 : 0
                }}>
                  <Button
                    variant="outlined"
                    onClick={handleSearch}
                    disabled={isSearching}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetSearch}
                    disabled={isSearching}
                    sx={{margin:'0 10px'}}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Box>
            {formError && (
              <Typography
                variant="body2"
                color="error"
                sx={{ mt: 1, fontSize: "0.875rem" }}
              >
                {formError}
              </Typography>
            )}
          </Box>

          <TableContainer
            component={Paper}
            className={classes.tableContainer}
            sx={{
              position: 'relative',
              minHeight: '200px',
              maxHeight: isMobile ? '60vh' : 'none',
              overflow: 'auto',
              boxShadow: isMobile ? 1 : 2
            }}
          >
            <Table
              className={classes.table}
              stickyHeader
              sx={{
                minWidth: isMobile ? '800px' : '650px',
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '10px' : '14px',
                  padding: isMobile ? '6px 8px' : '12px 16px',
                  whiteSpace: isMobile ? 'nowrap' : 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '100px' : '200px'
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '100px' : '150px'
                    }}
                  >
                    Company Name
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '120px' : '180px'
                    }}
                  >
                    Program Description
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Producer Code
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Created By
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Created Date
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Updated By
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Last Update
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    align="right"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '70px' : '100px'
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(isDeleting || isSearching) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                        <Loader />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : carriers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                        <Typography variant="subtitle1" color="textSecondary">
                          {isSearchActive ? 'No carriers found matching your search' : 'No Data Available'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  carriers.map((row) => (
                    <StyledTableRow className="styled-table-row" key={row.id}>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={row.agencyName || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(row.agencyName)}</span>
                          </Tooltip>
                        ) : (
                          row.agencyName || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={row.carrierProgramDescription || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(row.carrierProgramDescription, 20)}</span>
                          </Tooltip>
                        ) : (
                          row.carrierProgramDescription || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={row.producerCode || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(row.producerCode)}</span>
                          </Tooltip>
                        ) : (
                          row.producerCode || "N/A"
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {isMobile ? (
                          <Tooltip title={row.createdBy?.name || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(row.createdBy?.name || '-')}</span>
                          </Tooltip>
                        ) : (
                          row.createdBy?.name || '-'
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.createdBy?.timestamp
                          ? formatDate(row.createdBy.timestamp)
                          : '-'}
                      </TableCell>
                      <TableCell align="left">
                        {isMobile ? (
                          <Tooltip title={row.updatedBy?.name || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(row.updatedBy?.name || '-')}</span>
                          </Tooltip>
                        ) : (
                          row.updatedBy?.name || '-'
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.updatedBy?.timestamp
                          ? formatDate(row.updatedBy.timestamp)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            aria-label="edit"
                            className={classes.iconButton}
                            onClick={() => handleEdit(row)}
                            size={isMobile ? "small" : "medium"}
                            sx={{ p: isMobile ? '4px' : '8px' }}
                          >
                            <EditIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            className={classes.iconButton}
                            onClick={() => handleDeleteOpen(row.id)}
                            size={isMobile ? "small" : "medium"}
                            sx={{ p: isMobile ? '4px' : '8px' }}
                          >
                            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {totalElements > 0 && (
              <Box sx={{
                width: '100%',
                overflow: 'hidden',
                padding: isMobile ? '12px 0' : '16px 0',
              }}>
                <Stack
                  direction={isMobile ? "column" : "row"}
                  justifyContent="flex-end"
                  alignItems="center"
                  flexWrap="wrap"
                  spacing={2}
                >
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{
                      fontSize: isMobile ? '11px' : '14px',
                      marginRight: '8px'
                    }}>
                      Rows per page:
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      size={isMobile ? "small" : "medium"}
                      disabled={isSearching}
                      sx={{
                        fontSize: isMobile ? '11px' : '14px',
                        minWidth: isMobile ? '60px' : '80px',
                        height: isMobile ? '32px' : '36px',
                        '& .MuiSelect-select': {
                          padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                        }
                      }}
                    >
                      {[5, 10, 15, 20].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={handlePaginationChange}
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    size={isMobile ? "small" : "medium"}
                    disabled={isSearching}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: isMobile ? '12px' : '14px',
                        minWidth: isMobile ? '28px' : '32px',
                        height: isMobile ? '28px' : '32px'
                      }
                    }}
                  />
                </Stack>
              </Box>
            )}
          </TableContainer>

          <Dialog
            open={openDelete}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              handleCloseDelete(event, reason);
            }}
            maxWidth="sm"
            fullWidth={isMobile}
            PaperProps={{
              sx: {
                margin: isMobile ? 1 : 3,
                width: isMobile ? 'calc(100% - 16px)' : 'auto'
              }
            }}
          >
            <DialogTitle
              className="btn-primary"
              sx={{
                height: isMobile ? '56px' : '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile ? '0 12px' : '0 16px',
                fontSize: isMobile ? '16px' : '18px'
              }}
            >
              Delete Carrier
            </DialogTitle>
            <DialogContent>
              <Typography sx={{
                marginTop: '15px',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                Are you sure you want to delete this carrier? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{
              padding: isMobile ? '12px 16px' : '16px 24px',
              gap: 1
            }}>
              <Button
                onClick={handleCloseDelete}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="outlined"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: isMobile ? 'center' : 'right'
            }}
            sx={{
              top: isMobile ? '60px' : '24px',
              '& .MuiSnackbarContent-root': {
                fontSize: isMobile ? '12px' : '14px'
              }
            }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{
                whiteSpace: 'pre-line',
                fontSize: isMobile ? '12px' : '14px',
                width: isMobile ? '90vw' : 'auto',
                maxWidth: isMobile ? '350px' : '500px'
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManageCarrierListing;